import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { mediaUrl, waLink } from "@/lib/api";

export const Team = () => {
  const { t, L } = useI18n();
  const { data } = useSite();
  const s = data?.settings || {};
  const team = data?.team || [];

  return (
    <section id="equipo" data-testid="team-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">{t("team.eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-[0.95]">
              {t("team.title")}
            </h2>
          </div>
        </div>

        {team.length === 0 ? (
          <p className="mt-14 text-mut font-quote italic text-xl" data-testid="team-empty-state">
            {t("team.empty")}
          </p>
        ) : (
          <div data-testid="team-grid" className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {team.map((m, i) => (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="group"
                data-testid={`team-card-${m.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-panel border border-gold/10">
                  {m.photo_path ? (
                    <img
                      src={mediaUrl(m.photo_path)}
                      alt={m.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      style={{ filter: "saturate(0.8) contrast(1.05)" }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" data-testid={`team-monogram-${m.id}`}>
                      <span className="font-display text-7xl text-gold/25 group-hover:text-gold/50 transition-colors">
                        {(m.name || "?").charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                </div>
                <div className="flex items-end justify-between pt-4 gap-3">
                  <div>
                    <h3 className="font-display text-xl text-bone">{m.name}</h3>
                    <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-gold/80 mt-1">
                      {L(m.role_es, m.role_en)}
                    </p>
                  </div>
                  <a
                    href={waLink(s.whatsapp, `Hola Blackbeard Barbería, quisiera agendar con ${m.name}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`team-book-${m.id}`}
                    title={`${t("team.book_with")} ${m.name}`}
                    className="shrink-0 h-10 w-10 rounded-full border border-gold/30 text-gold flex items-center justify-center hover:bg-gold hover:text-ink transition-colors"
                  >
                    <MessageCircle size={16} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Team;
